// Removed FaSearch import
import { useQuery } from "react-query";
import axios from "axios";
import { Button, SecondaryButton } from "@jammable/ui-core";
import VoiceCard from "./VoiceCard";
// import VoiceImage from "../VoiceImage"; // Removed unused import
// import { FaTrash } from "react-icons/fa6"; // Removed unused import
import { Input } from "@jammable/ui-components/Forms";
import { useState, useEffect, useMemo } from "react";
import debounce from "lodash/debounce";
import BackButton from "../common/BackButton";
// import VoiceImageNoBackground from "../VoiceImageNoBackground"; // Removed unused import
// import { FaCross } from "react-icons/fa"; // Removed unused import
import { getAuth } from "firebase/auth"; // Added firebase auth import
import { useAuth } from "../../context/AuthProvider"; // Added useAuth import
import { HiRefresh } from "react-icons/hi"; // Import HiRefresh
import { FaStar, FaClock, FaTimes } from "react-icons/fa"; // Removed unused FaCheckSquare, Added FaTimes
import VoiceImage from "../VoiceImage";

// Skeleton Component for Loading State
const SkeletonVoiceCard = () => (
  <div className="tw-w-full tw-h-28 tw-bg-gray-100 tw-rounded-2xl tw-animate-pulse tw-flex tw-flex-col tw-items-center tw-p-2 tw-gap-2"></div>
);

const VoiceSelector = ({ selectedVoices, onSelectVoice, onBack }) => {
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [activeTab, setActiveTab] = useState("recommended"); // 'recommended', 'recent', 'selected'
  const limit = 10; // Limit for results
  const { user } = useAuth(); // Get user from auth context
  const [displayLimit, setDisplayLimit] = useState(9); // Default limit for smaller screens

  // Debounce search input
  useEffect(() => {
    const handler = debounce(() => {
      setDebouncedSearch(search);
    }, 500); // 500ms debounce time

    handler();
    return () => {
      handler.cancel(); // Cleanup debounce on unmount or search change
    };
  }, [search]);

  // Effect to handle screen resize and adjust display limit
  useEffect(() => {
    const handleResize = () => {
      // Assuming 'md' breakpoint is 768px (common Tailwind default)
      const limit = window.innerWidth >= 768 ? 10 : 9;
      setDisplayLimit(limit);
    };

    // Set initial limit on mount
    handleResize();

    // Add event listener for window resize
    window.addEventListener("resize", handleResize);

    // Clean up event listener on component unmount
    return () => window.removeEventListener("resize", handleResize);
  }, []); // Empty dependency array ensures this runs only on mount and unmount

  // --- API Fetching Functions ---

  // Fetch Recommended: Duets if voices selected, otherwise popular
  const fetchRecommendedVoices = async () => {
    if (selectedVoices && selectedVoices.length > 0) {
      // Fetch Duet Matches
      const modelIds = selectedVoices.map((v) => v._id);
      const queryString = `modelIds=${modelIds.join(",")}`;
      const { data } = await axios.get(
        `/api/model/getDuetMatches?${queryString}`,
      );
      return data?.models?.slice(0, 10) ?? []; // Limit to 10 recommendations
    } else {
      // Fetch Popular Voices (Example: sorted by uses)
      const { data } = await axios.get(`/api/model`, {
        params: { limit: 10, sortOrder: "desc", sortBy: "usesLast24Hours" },
      });
      return data?.data?.slice(0, 10) ?? []; // Ensure only top 10 are returned
    }
  };

  // Fetch Recent Voices (Requires Auth)
  const fetchRecentVoices = async () => {
    try {
      const auth = getAuth();
      const token = await auth.currentUser?.getIdToken();
      if (!token) {
        console.warn("No auth token found for fetchRecentVoices");
        return []; // Or throw an error if auth is strictly required
      }
      const { data } = await axios.get(
        `/api/home/getRecentVoices?limit=${limit}`,
        {
          headers: { Authorization: token },
        },
      );
      return data?.voices ?? [];
    } catch (error) {
      console.error("Failed to fetch recent voices:", error);
      // Check if it's a 404 or similar indicating the endpoint might not exist
      if (error.response?.status === 404) {
        console.warn("'/api/home/getRecentVoices' endpoint not found.");
        // Optionally return a specific indicator or handle it gracefully
      }
      throw error; // Re-throw error for react-query to handle
    }
  };

  // Fetch Search Results
  const fetchSearchedVoices = async (searchTerm) => {
    if (!searchTerm) return []; // Don't fetch if search is empty
    const { data } = await axios.get(`/api/model`, {
      params: {
        search: searchTerm,
        limit,
        page: 1,
        sortOrder: "desc",
        sortBy: "usesLast24Hours", // Or relevance?
      },
    });
    return data?.data ?? [];
  };

  // --- React Query Hooks ---

  const selectedVoiceIds = useMemo(
    () => selectedVoices.map((v) => v._id).join(","),
    [selectedVoices],
  );

  // Query for Recommended Voices
  const {
    data: recommendedData,
    isFetching: isFetchingRecommended,
    isLoading: isLoadingRecommended,
    error: recommendedError,
    isError: isRecommendedError,
  } = useQuery(
    ["recommendedVoices", selectedVoiceIds], // Key changes based on selected voices
    fetchRecommendedVoices, // Revert back to original function call
    {
      enabled: !debouncedSearch && activeTab === "recommended",
      keepPreviousData: true,
      initialData: [],
      onError: (err) =>
        console.error("Failed to fetch recommended voices:", err),
    },
  );

  // Query for Recent Voices
  const {
    data: recentData,
    isFetching: isFetchingRecent,
    isLoading: isLoadingRecent,
    error: recentError,
    isError: isRecentError,
  } = useQuery(["recentVoices"], fetchRecentVoices, {
    enabled: !!user && !debouncedSearch && activeTab === "recent",
    keepPreviousData: true,
    initialData: [],
    onError: (err) => console.error("Failed to fetch recent voices:", err),
    // Consider adding retry: false if 404s are expected/handled
  });

  // Query for Search Results
  const {
    data: searchData,
    isFetching: isFetchingSearch,
    isLoading: isLoadingSearch,
    error: searchError,
    isError: isSearchError,
  } = useQuery(
    ["searchVoices", debouncedSearch],
    () => fetchSearchedVoices(debouncedSearch),
    {
      enabled: !!debouncedSearch,
      keepPreviousData: true,
      initialData: [],
      onError: (err) => console.error("Failed to fetch search results:", err),
    },
  );

  // --- Determine Display Logic ---
  const {
    currentData,
    isLoading,
    isFetching,
    isError,
    fetchError,
    headerText,
  } = useMemo(() => {
    if (debouncedSearch) {
      return {
        currentData: searchData,
        isLoading: isLoadingSearch,
        isFetching: isFetchingSearch,
        isError: isSearchError,
        fetchError: searchError,
        headerText: "Search Results",
      };
    }

    switch (activeTab) {
      case "recent":
        return {
          currentData: recentData,
          isLoading: isLoadingRecent,
          isFetching: isFetchingRecent,
          isError: isRecentError,
          fetchError: recentError,
          headerText: "Recent",
        };
      case "recommended":
      default: {
        return {
          currentData: recommendedData,
          isLoading: isLoadingRecommended,
          isFetching: isFetchingRecommended,
          isError: isRecommendedError,
          fetchError: recommendedError,
          headerText:
            selectedVoices.length > 0 ? "Recommended Duets" : "Popular Voices",
        };
      }
    }
  }, [
    debouncedSearch,
    activeTab,
    selectedVoices,
    searchData,
    isLoadingSearch,
    isFetchingSearch,
    isSearchError,
    searchError,
    recentData,
    isLoadingRecent,
    isFetchingRecent,
    isRecentError,
    recentError,
    recommendedData,
    isLoadingRecommended,
    isFetchingRecommended,
    isRecommendedError,
    recommendedError,
  ]);

  // Calculate refreshing state separately for the spinner icon
  const isRefreshing = isFetching && !isLoading;

  // Determine if we should show the initial skeleton loader
  // Show skeleton if:
  // 1. Search is active and loading initially
  // 2. Recent tab is active and loading initially
  // 3. Recommended tab is active, loading initially, AND has no data yet

  const showSkeletonLoader =
    (debouncedSearch &&
      isFetchingSearch &&
      (!searchData || searchData.length === 0)) ||
    (activeTab === "recent" &&
      isFetchingRecent &&
      (!recentData || recentData.length === 0)) ||
    (activeTab === "recommended" &&
      !debouncedSearch && // Make sure search is not active
      isFetchingRecommended && // Use isFetching instead of isLoading
      (!recommendedData || recommendedData.length === 0));

  // Effect to switch tab if selected becomes empty
  useEffect(() => {
    if (selectedVoices.length === 0 && activeTab === "selected") {
      setActiveTab("recommended");
    }
  }, [selectedVoices.length, activeTab]);

  const isVoiceSelected = (voice) =>
    selectedVoices.some((v) => v._id === voice._id);

  // Tab Button Component
  const TabButton = ({ id, label, icon: Icon, isActive, onClick }) => (
    <SecondaryButton
      onClick={() => onClick(id)}
      // Adjusted padding and label visibility for mobile
      className={`tw-flex-1 tw-h-10 tw-flex tw-items-center tw-justify-center tw-gap-2 tw-p-2 md:tw-px-5 md:tw-py-2 tw-rounded-full tw-text-sm tw-font-semibold tw-transition-all tw-duration-150 tw-border ${
        isActive
          ? "tw-bg-purple-100 tw-text-purple-700 tw-border-purple-100"
          : "tw-bg-white tw-text-black/50 tw-border-gray-300 hover:tw-border-gray-400 hover:tw-text-black/70"
      }`}
    >
      <Icon size={16} />
      {/* Hide label on small screens (mobile), show on medium and up */}
      <span className="tw-hidden md:tw-inline">{label}</span>
    </SecondaryButton>
  );

  return (
    // New top-level container with grey background and rounded corners
    <div className="tw-bg-neutral-100 tw-rounded-3xl tw-overflow-hidden tw-flex tw-flex-col tw-h-full">
      {/* White content container */}
      <div className="tw-bg-white tw-p-4 tw-rounded-t-3xl tw-rounded-b-3xl tw-shadow-b-3xl tw-border-b tw-border-gray-200 tw-flex-grow">
        {" "}
        {/* Removed padding */}
        {/* Header with Back button and Selected Voices */}
        <div className="tw-flex tw-items-center tw-gap-3 tw-mb-2">
          <BackButton
            onClick={onBack}
            aria-label="Back to main create options"
          />
          <div className="tw-text-xl  tw-tracking-widest tw-font-black tw-text-black/40 tw-uppercase tw-flex tw-gap-3 tw-items-center">
            Voices
          </div>
        </div>
        {/* Search Input */}
        <Input
          label="Search"
          placeholder="Search for a voice..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="tw-mb-2 tw-rounded-full"
        />
        {/* Tabs - Show only when search is inactive */}
        {!debouncedSearch && (
          <div className="tw-flex tw-gap-2 tw-mb-2">
            <TabButton
              id="recommended"
              label="Recommended"
              icon={FaStar}
              isActive={activeTab === "recommended"}
              onClick={setActiveTab}
            />
            <TabButton
              id="recent"
              label="Recent"
              icon={FaClock}
              isActive={activeTab === "recent"}
              onClick={setActiveTab}
            />
          </div>
        )}
        {/* Header - Changes based on search state or active tab */}
        <h3 className="tw-flex tw-items-center tw-gap-2 tw-text-sm tw-uppercase tw-font-black tw-tracking-widest tw-text-black/40">
          <span>{headerText}</span>
          {isRefreshing && (
            <HiRefresh
              className="tw-animate-spin tw-text-purple-700"
              size={16}
            />
          )}
        </h3>
        {/* Container for voice list, loading, and error states */}
        <div className="tw-mt-2">
          {showSkeletonLoader ? (
            <div className="tw-grid tw-pt-2 tw-max-h-[275px] tw-overflow-y-auto tw-grid-cols-3 sm:tw-grid-cols-3 md:tw-grid-cols-5 tw-gap-2 tw-justify-start tw-w-full">
              {[...Array(displayLimit)].map((_, index) => (
                <SkeletonVoiceCard key={index} />
              ))}
            </div>
          ) : isError ? (
            <div className="tw-flex tw-justify-center tw-items-center tw-h-full tw-p-4">
              <p className="tw-text-red-600 tw-text-center">
                {fetchError?.response?.data?.message ||
                  `Could not load ${headerText.toLowerCase()}. Please try again later.`}
              </p>
            </div>
          ) : currentData.length > 0 ? (
            <div className="tw-grid tw-pt-2 tw-max-h-[275px] tw-overflow-y-auto md:tw-overflow-y-visible tw-grid-cols-3 sm:tw-grid-cols-3 md:tw-grid-cols-5 tw-gap-2 tw-justify-start tw-w-full">
              {currentData.slice(0, displayLimit).map((voice) => (
                <VoiceCard
                  key={voice._id}
                  voice={voice}
                  isSelected={isVoiceSelected(voice)}
                  onClick={() => onSelectVoice(voice)}
                />
              ))}
            </div>
          ) : (
            !isLoading &&
            !isFetching &&
            currentData.length === 0 && (
              <div className="tw-flex tw-justify-center tw-items-center tw-h-full tw-p-4">
                <p className="tw-text-gray-500">
                  {debouncedSearch
                    ? "No voices found matching your search."
                    : activeTab === "selected"
                      ? "No voices selected yet."
                      : `No ${headerText.toLowerCase()} found.`}
                </p>
              </div>
            )
          )}
        </div>
      </div>{" "}
      {/* End white content container */}
      {/* Footer with Confirm Button */}
      {/* Now sits directly inside the grey container */}
      <div className="tw-flex tw-justify-center tw-p-2 md:tw-p-4 tw-gap-2">
        {" "}
        <div className="tw-flex tw-w-full tw-items-center tw-gap-1 tw-border tw-border-gray-200 tw-border-solid tw-rounded-full tw-pl-1 ">
          <div className="tw-flex tw-flex-shrink-0 tw-pr-1">
            {selectedVoices?.map((voice, index) => {
              const isRemovable = selectedVoices.length > 1;
              return (
                <div
                  key={voice._id}
                  className={`tw-flex tw-relative ${isRemovable ? "tw-cursor-pointer hover:tw-opacity-80" : ""} tw-transition-opacity`}
                  onClick={() => isRemovable && onSelectVoice(voice)}
                  aria-label={
                    isRemovable
                      ? `Remove ${voice.model_name || "voice"}`
                      : voice.model_name || "voice"
                  }
                  title={
                    isRemovable
                      ? `Remove ${voice.model_name || "voice"}`
                      : voice.model_name || "voice"
                  }
                >
                  <VoiceImage
                    key={voice?._id}
                    model={voice}
                    size={42}
                    className="tw-rounded-full tw-border-2 tw-border-solid tw-border-white"
                    style={{
                      marginLeft: index === 0 ? 0 : -15,
                    }}
                  />
                  {isRemovable && (
                    <div
                      className="tw-absolute tw-size-4 tw-flex tw-items-center tw-justify-center tw-top-0
                tw-right-0   tw-bg-black tw-rounded-full tw-p-0.5 tw-pointer-events-none tw-z-10"
                    >
                      <FaTimes size={10} className="tw-text-white" />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          <Button
            onClick={onBack}
            className="tw-flex-grow tw-w-full tw-text-lg tw-rounded-full"
          >
            {selectedVoices.length === 0 ? "Select a Voice" : "Continue"}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default VoiceSelector;
